// Login.jsx — ARQUIVO COMPLETO
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Eye,
  EyeOff,
  User,
  Building2,
  ShieldCheck,
} from "lucide-react";

import "./Login.scss";
import bgImage from "../../assets/auth-background.jpg";
import { login } from "../../services/authService";

import { usePushNotifications } from "../../hooks/usePushNotifications";
import { NotificationPermissionPrompt } from "../../Components/NotificationPermissionPrompt/NotificationPermissionPrompt";
import { FREELANCERS_BLOCKED } from "../../config/launchConfig";
import FreelancerLaunchNotice from "../../Components/FreelancerLauchNotice/FreelancerLauchNotice";

export default function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [accountType, setAccountType] = useState("freelancer");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showAdminGate, setShowAdminGate] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [loggedUser, setLoggedUser] = useState(null);

  const {
    requestPermission,
    isSubscribed,
    permission,
  } = usePushNotifications();

  // ============================================================
  // NAVEGAÇÃO DE ACORDO COM O TIPO DE USUÁRIO
  // ============================================================

  const navigateUser = (user) => {
    // Se veio de um link de notificação (?tab=...), abre direto na aba certa
    const search = window.location.search;

    if (!user) {
      alert(
        "Não foi possível identificar o tipo da conta. Entre em contato com o suporte."
      );
      return;
    }

    if (user.userType === "Company") {
      navigate("/dashboard-company" + search);
      return;
    }

    if (user.userType === "Admin") {
      setShowAdminGate(true);
      return;
    }

    if (user.userType === "Freelancer") {
      navigate("/dashboard-freelancer" + search);
      return;
    }

    if (
      user.userType === "freelancer" ||
      user.userType === "Freelance"
    ) {
      navigate("/dashboard-freelancer" + search);
      return;
    }

    alert(
      "Conta em análise ou tipo de usuário não reconhecido. Entre em contato com o suporte."
    );
  };

  // ============================================================
  // LOGIN
  // ============================================================

  const handleLogin = async (e) => {
    e.preventDefault();

    setError("");
    setLoading(true);

    try {
      const user = await login(email, password);

      if (!user) {
        throw new Error("Usuário não retornado pelo servidor.");
      }

      setLoggedUser(user);

      if (user.userType === "Admin") {
        setShowAdminGate(true);
        return;
      }

      // Alguns navegadores in-app (webview do Instagram/Facebook/TikTok,
      // Android mais antigo) não implementam a API Notification.
      // Sem essa checagem, o acesso a Notification.permission lança
      // "Notification is not defined" e a pessoa nunca é redirecionada.
      const notificationsSupported =
        typeof window !== "undefined" && "Notification" in window;

      if (notificationsSupported) {
        if (Notification.permission === "default") {
          setShowNotificationPrompt(true);
          return;
        }

        if (Notification.permission === "granted") {
          try {
            await requestPermission();
          } catch (permissionError) {
            console.error(
              "[LOGIN] Erro ao solicitar permissão de notificação:",
              permissionError
            );
          }
          navigateUser(user);
          return;
        }

        if (Notification.permission === "denied") {
          navigateUser(user);
          return;
        }
      }

      navigateUser(user);
    } catch (error) {
      console.error("[LOGIN] Erro:", error);

      // Garantir que o erro é uma string, não um objeto
      let errorMessage = "Erro ao conectar com o servidor";
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error?.response?.data === "string") {
        errorMessage = error.response.data;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // ACEITAR NOTIFICAÇÕES
  // ============================================================

  const handleAcceptNotifications = async () => {
    setShowNotificationPrompt(false);

    try {
      await requestPermission();
    } catch (error) {
      // silencioso
    }

    navigateUser(loggedUser);
  };

  // ============================================================
  // RECUSAR NOTIFICAÇÕES
  // ============================================================

  const handleDeclineNotifications = () => {
    setShowNotificationPrompt(false);
    navigateUser(loggedUser);
  };

  // ============================================================
  // DEIXAR PARA DEPOIS
  // ============================================================

  const handleLaterNotifications = () => {
    setShowNotificationPrompt(false);
    navigateUser(loggedUser);
  };

  // ============================================================
  // ADMIN GATE
  // ============================================================

  if (showAdminGate) {
    return (
      <div
        className="login-page"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <div className="login-card">
          <div className="login-header">
            <ShieldCheck size={32} />
            <h1>Olá, Chefe</h1>
            <p>Detectamos que essa conta é de Administrador.</p>
          </div>

          <div className="admin-gate">
            <p className="admin-gate__question">
              Deseja entrar como Administrador?
            </p>

            <button
              type="button"
              className="login-button"
              onClick={() => navigate("/dashboard-admin")}
            >
              <ShieldCheck size={18} />
              Entrar como Administrador
            </button>

            <p className="admin-gate__or">ou continue como:</p>

            <div className="admin-gate__options">
              <button
                type="button"
                className="type-btn"
                onClick={() => navigate("/dashboard-freelancer")}
              >
                <User size={18} />
                Freelancer
              </button>

              <button
                type="button"
                className="type-btn"
                onClick={() => navigate("/dashboard-company")}
              >
                <Building2 size={18} />
                Empresa
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // LOGIN
  // ============================================================

  return (
    <div
      className="login-page"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <div className="login-card">
        <div className="login-header">
          <h1>LOGIN</h1>
          <p>Entre na sua conta para continuar</p>
        </div>

        <div className="auth-tabs">
          <button className="tab-btn active">Entrar</button>

          <Link
            to={accountType === "empresa" ? "/register-company" : "/register"}
            className="tab-btn"
          >
            Cadastrar
          </Link>
        </div>

        <div className="account-type">
          <label>Você é:</label>

          <div className="type-options">
            <button
              type="button"
              className={`type-btn ${accountType === "freelancer" ? "active" : ""}`}
              onClick={() => setAccountType("freelancer")}
            >
              <User size={18} />
              Freelancer
            </button>

            <button
              type="button"
              className={`type-btn ${accountType === "empresa" ? "active" : ""}`}
              onClick={() => setAccountType("empresa")}
            >
              <Building2 size={18} />
              Empresa
            </button>
          </div>
        </div>

        <>
          {error && (
            <div className="login-error">
              <span>{error}</span>
            </div>
          )}

          <form className="login-form" onSubmit={handleLogin}>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label>Senha</label>
              <div className="password-input">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Link to="/forgot-password" className="login-forgot-link">
              Esqueci minha senha
            </Link>

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? (
                <>
                  <span className="login-spinner" />
                  Entrando...
                </>
              ) : (
                "Entrar Agora"
              )}
            </button>
          </form>
        </>

        <div className="login-footer">
          <p>Conectando talentos com oportunidades</p>
        </div>

        <div style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.75rem', color: '#94a3b8' }}>
          <Link to="/termos-de-uso" style={{ color: 'inherit' }}>Termos de Uso</Link>
          {' · '}
          <Link to="/politica-de-privacidade" style={{ color: 'inherit' }}>Política de Privacidade</Link>
        </div>
      </div>

      <NotificationPermissionPrompt
        isOpen={showNotificationPrompt}
        onAccept={handleAcceptNotifications}
        onDecline={handleDeclineNotifications}
        onLater={handleLaterNotifications}
        context="receber avisos quando faltar 1 hora para seus serviços começarem"
      />
    </div>
  );
}